/**
 * Internal rank scale: Iron 1 = 1 … Immortal 3 = 24, Radiant = 25.
 * valorant-api.com / Riot use tiers 3 (Iron 1) … 27 (Radiant); 0–2 are unranked/unused.
 */
export const MIN_RANK_TIER = 1;
export const MAX_RANK_TIER = 25;

const DIVISIONS = [
  'Iron',
  'Bronze',
  'Silver',
  'Gold',
  'Platinum',
  'Diamond',
  'Ascendant',
  'Immortal',
];

export interface RankTierInfo {
  tier: number;
  name: string;
  division: string;
}

export const RANK_TIERS: readonly RankTierInfo[] = [
  ...DIVISIONS.flatMap((division, d) =>
    [1, 2, 3].map((step) => ({ tier: d * 3 + step, name: `${division} ${step}`, division })),
  ),
  { tier: 25, name: 'Radiant', division: 'Radiant' },
];

const API_TIER_OFFSET = 2;

export function isValidRankTier(tier: unknown): tier is number {
  return (
    Number.isInteger(tier) && (tier as number) >= MIN_RANK_TIER && (tier as number) <= MAX_RANK_TIER
  );
}

/** Converts a Riot / valorant-api competitive tier (3–27) to the internal scale (1–25). Returns null for unranked. */
export function fromApiTier(apiTier: number | null | undefined): number | null {
  if (apiTier == null) return null;
  const tier = apiTier - API_TIER_OFFSET;
  return isValidRankTier(tier) ? tier : null;
}

export function toApiTier(tier: number): number {
  return tier + API_TIER_OFFSET;
}

export function rankName(tier: number | null | undefined): string {
  if (tier == null) return 'Unranked';
  return RANK_TIERS[tier - 1]?.name ?? 'Unranked';
}

/**
 * Resolves a tier as returned by HenrikDev / Riot ({ id, name }).
 * The name is preferred: older seasons (before Ascendant, Episode 5) use a different id table,
 * but names like "Immortal 1" are unambiguous. Unknown names (e.g. "Unrated") → null.
 */
export function rankTierFromApi(
  tier: { id?: number | null; name?: string | null } | null | undefined,
): number | null {
  if (!tier) return null;
  if (tier.name) return rankTierFromName(tier.name);
  return fromApiTier(tier.id);
}

/** Parses names like "Gold 2", "Immortal 3", "Radiant" (case-insensitive). */
export function rankTierFromName(name: string | null | undefined): number | null {
  if (!name) return null;
  const normalized = name.trim().toLowerCase();
  return RANK_TIERS.find((r) => r.name.toLowerCase() === normalized)?.tier ?? null;
}
